import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Accordion } from "../ui/Accordion";
import type { CustomerOrder, PreOrderItem } from "../../../common/jsonRpcTypes";
import { formatBookingDate, formatCurrency } from "../../../utils/countryLocaleMap";
import { ExclamationTriangleFill, Trash3 } from "../icons";
import ItemOptionsDisplay from "../shared/ItemOptionsDisplay";
import AlertBanner from "../shared/AlertBanner";
import DietaryTags from "../shared/DietaryTags";
import PrimaryButton from "../buttons/primaryButton";
import { dietaryRequirementKey, partitionDietaryRequirements } from "./orderDietaryInfo";

interface OrderItemProps {
  order: CustomerOrder;
  onDelete: (orderId: string) => void;
  onEdit?: (orderId: string) => void;
  canDelete: boolean;
  canEdit?: boolean;
  showActions: boolean;
  currency: string;
  countryCode: string;
  hidePaid: boolean;
  defaultExpanded?: boolean;
}

interface OrderItemTotalsProps {
  order: CustomerOrder;
  currency: string;
  countryCode: string;
}

function OrderItemTotals({ order, currency, countryCode }: Readonly<OrderItemTotalsProps>) {
  const { t } = useTranslation();
  const isTaxExclusive =
    order.tax_inclusive === false && Array.isArray(order.tax_breakdown) && order.tax_breakdown.length > 0;
  const taxLabel = order.tax_label ?? "VAT";

  const serviceChargeRow =
    (order.service_charge_percentage ?? 0) > 0 ? (
      <div className="gcw:flex gcw:justify-between gcw:mb-0">
        <span className="gcw:text-order-details">
          {t("service_charge", "Service Charge")} ({order.service_charge_percentage}%):
        </span>
        <span className="gcw:text-order-details">
          {formatCurrency(order.service_charge_amount ?? 0, currency, countryCode)}
        </span>
      </div>
    ) : null;

  if (isTaxExclusive) {
    return (
      <>
        <div className="gcw:flex gcw:justify-between gcw:mb-0">
          <span className="gcw:text-order-details">{t("net_label", "Net")}:</span>
          <span className="gcw:text-order-details">
            {formatCurrency(order.total_net ?? order.total_cost, currency, countryCode)}
          </span>
        </div>
        {serviceChargeRow}
        {order.tax_breakdown!.map((entry) => (
          <div key={entry.label} className="gcw:flex gcw:justify-between gcw:mb-0">
            <span className="gcw:text-order-details">{entry.label}:</span>
            <span className="gcw:text-order-details">{formatCurrency(entry.amount, currency, countryCode)}</span>
          </div>
        ))}
        <div className="gcw:flex gcw:justify-between gcw:mb-0">
          <p className="gcw:text-order-details gcw:text-text-primary gcw:mb-0">{t("tax_total_label", "Total")}:</p>
          <p className="gcw:text-order-details gcw:text-text-primary gcw:mb-0">
            {formatCurrency(
              order.total_gross ?? order.total_cost_with_service ?? order.total_cost,
              currency,
              countryCode,
            )}
          </p>
        </div>
      </>
    );
  }

  return (
    <>
      {serviceChargeRow}
      <div className="gcw:flex gcw:justify-between gcw:mb-0">
        <p className="gcw:text-order-details gcw:text-text-primary gcw:mb-0">
          {t("total", "Total")} ({t("inc_vat", "inc {{taxLabel}}", { taxLabel })}):
        </p>
        <p className="gcw:text-order-details gcw:text-text-primary gcw:mb-0">
          {formatCurrency(order.total_cost_with_service ?? order.total_cost, currency, countryCode)}
        </p>
      </div>
    </>
  );
}

const OrderItem: React.FC<OrderItemProps> = ({
  order,
  onDelete,
  onEdit,
  canDelete,
  canEdit = false,
  showActions,
  currency,
  countryCode,
  hidePaid,
  defaultExpanded = false,
}) => {
  const { t } = useTranslation();
  const [activeKey, setActiveKey] = useState<string | undefined>(defaultExpanded ? "0" : undefined);
  const showButtons = !order.payment_ref;
  const actionButtonClassName =
    "gcw:flex-1 gcw:inline-flex gcw:items-center gcw:justify-center gcw:rounded-md gcw:bg-primary gcw:text-accent-fg gcw:border gcw:border-primary gcw:px-bs-3 gcw:font-medium gcw:transition-colors gcw:hover:bg-primary-hover gcw:focus:outline-none gcw:focus:ring-2 gcw:focus:ring-primary gcw:focus:ring-offset-2 gcw:text-lg";

  const getStatusBadge = (status: string) => {
    const statusClasses: Record<string, string> = {
      pending: "gcw:text-dark-grey gcw:text-md gcw:font-base",
      complete: "gcw:text-pill-text-complete gcw:text-md gcw:font-base",
      deleted:
        "gcw:bg-gray-500 gcw:text-white gcw:border gcw:rounded-md gcw:border-gray-500 gcw:text-body gcw:font-base",
    };

    return (
      <span
        className={`gcw:inline-flex gcw:items-center gcw:rounded-full gcw:px-2 gcw:py-0.5 gcw:font-medium ${statusClasses[status] ?? "gcw:bg-gray-500 gcw:text-white gcw:text-xs"}`}
      >
        {t(`order_status_${status}`, `${status.charAt(0).toUpperCase()}${status.slice(1)}`)}
      </span>
    );
  };

  // Group items by for_name for multi-person orders
  const groupItemsByName = (items: PreOrderItem[]): Map<string | undefined, PreOrderItem[]> => {
    const grouped = new Map<string | undefined, PreOrderItem[]>();

    for (const item of items) {
      const key = item.for_name;
      const existing = grouped.get(key) ?? [];
      existing.push(item);
      grouped.set(key, existing);
    }

    return grouped;
  };

  const groupItems = (items: PreOrderItem[]): Map<string | undefined, PreOrderItem[]> => {
    const grouped = new Map<string | undefined, PreOrderItem[]>();

    for (const item of items) {
      const key = "no-name";
      const existing = grouped.get(key) ?? [];
      existing.push(item);
      grouped.set(key, existing);
    }

    return grouped;
  };

  const hasMultipleNames = order.items.some((item) => item.for_name);
  const groupedItems = hasMultipleNames ? groupItemsByName(order.items) : groupItems(order.items);
  const dietaryRequirements = order.dietary_requirements ?? [];
  const isMultiPersonOrder = groupedItems.size > 1;
  const groupNames = new Set(Array.from(groupedItems.keys()).filter((name): name is string => name !== undefined));
  const { topRequirements: unmatchedRequirements, requirementsByName } = partitionDietaryRequirements(
    dietaryRequirements,
    groupNames,
  );
  const topRequirements = isMultiPersonOrder ? unmatchedRequirements : dietaryRequirements;
  const hasDietaryInfo = !!order.customer_notes || (order.dietary_requirements?.length ?? 0) > 0;
  const showTopPersonNames = isMultiPersonOrder || dietaryRequirements.length > 1;

  const renderDietaryTags = (requirements: CustomerOrder["dietary_requirements"], showPersonName: boolean) => {
    const occurrences = new Map<string, number>();
    return requirements?.map((requirement) => {
      const key = dietaryRequirementKey(requirement);
      const occurrence = occurrences.get(key) ?? 0;
      occurrences.set(key, occurrence + 1);
      return (
        <DietaryTags
          key={`${key}:${occurrence}`}
          dietTypes={requirement.diet_types}
          allergies={requirement.allergies}
          personName={showPersonName ? requirement.name : undefined}
          className="gcw:mb-2"
        />
      );
    });
  };

  const renderItemRow = (item: PreOrderItem, index: number) => (
    <li key={`${order.id}-item-${index}`} className="gcw:flex gcw:justify-between gcw:mb-1 gcw:text-order-details">
      <div>
        <span>
          {item.quantity}x {item.name}
        </span>
        <ItemOptionsDisplay
          options={item.options}
          currency={currency}
          countryCode={countryCode}
          className="item-options"
          labelClassName="item-options-label"
          valueClassName="item-options-value"
        />
      </div>
      <span className="gcw:text-dark-grey">{formatCurrency(item.total, currency, countryCode)}</span>
    </li>
  );

  const renderItemsList = () => {
    if (!groupedItems) {
      return <span className="ordered-for-label">{t("no_items", "No items ordered")}</span>;
    }

    if (groupedItems && groupedItems.size === 1) {
      // No multi-person ordering - render items directly
      return (
        <ul className="gcw:list-none gcw:pl-0 gcw:mb-0">
          {order.items.map((item, index) => renderItemRow(item, index))}
        </ul>
      );
    }

    // Multi-person ordering - group by name
    const entries = Array.from(groupedItems.entries());
    return (
      <>
        {entries.map(([name, items], groupIndex) => (
          <div key={`${groupIndex}-${name ?? "no-name"}`} className="order-name-group">
            {name && <div className="ordered-for-label">{t("ordered_for", "Ordered for {{name}}", { name })}</div>}
            {name && renderDietaryTags(requirementsByName.get(name), false)}
            <ul className="gcw:list-none gcw:pl-0 gcw:mb-0">
              {items.map((item, index) => renderItemRow(item, groupIndex * 100 + index))}
            </ul>
          </div>
        ))}
      </>
    );
  };

  const handleAccordionToggle = () => {
    setActiveKey((prev) => (prev === "0" ? undefined : "0"));
  };

  return (
    <div className="order-item gcw:mb-4 gcw:bg-white">
      <Accordion activeKey={activeKey}>
        <Accordion.Item eventKey="0">
          <Accordion.Header onClick={handleAccordionToggle}>
            <div className="order-item-header">
              <div className="order-item-header-left">
                <h5 className="gcw:inline-flex gcw:items-center gcw:gap-1 gcw:text-body gcw:mb-0 gcw:font-bold">
                  {order.name}
                  {hasDietaryInfo && (
                    <>
                      <ExclamationTriangleFill
                        aria-hidden="true"
                        focusable="false"
                        size={16}
                        className="gcw:text-error gcw:shrink-0"
                        data-testid="order-dietary-warning-icon"
                      />
                      <span className="gcw:sr-only">
                        {t("order_has_dietary_info", "This order has notes or dietary requirements")}
                      </span>
                    </>
                  )}
                </h5>
                <small className="gcw:text-order-details">
                  {formatBookingDate(order.created_date, countryCode, "short")}
                </small>
              </div>
              <div className="order-item-header-right">{getStatusBadge(order.status)}</div>
            </div>
          </Accordion.Header>
          <Accordion.Body>
            {topRequirements.length > 0 && (
              <div className="gcw:mb-bs-3">{renderDietaryTags(topRequirements, showTopPersonNames)}</div>
            )}
            {order.customer_notes && (
              <AlertBanner
                variant="warning"
                role="note"
                className="gcw:mt-bs-3 gcw:mb-bs-4 gcw:w-full"
                contentClassName="gcw:w-full gcw:p-0"
                textClassName="gcw:min-w-0 gcw:break-words gcw:whitespace-pre-line"
              >
                <>
                  <strong className="order-note-label">{t("order_notes_label", "Notes:")}</strong>{" "}
                  {order.customer_notes}
                </>
              </AlertBanner>
            )}
            {order.items.length > 0 && <div className="order-items-section">{renderItemsList()}</div>}

            {showActions && showButtons && (
              <div className="gcw:pt-bs-3 gcw:flex gcw:gap-4 gcw:w-full">
                {canEdit && onEdit && (
                  <PrimaryButton
                    type="button"
                    className={actionButtonClassName}
                    onClick={() => onEdit(order.id)}
                    ariaLabel={t("edit_order", "Edit order")}
                    text={t("edit_order", "Edit order")}
                  />
                )}
                {canDelete && (
                  <PrimaryButton
                    type="button"
                    className="gcw:group gcw:inline-flex gcw:items-center gcw:justify-center gcw:rounded-md gcw:border gcw:border-error gcw:text-error gcw:bg-transparent gcw:px-bs-4 gcw:py-2 gcw:font-medium gcw:transition-colors gcw:hover:bg-error gcw:hover:text-white"
                    onClick={() => onDelete(order.id)}
                    ariaLabel={t("delete_order", "Delete order")}
                    icon={
                      <Trash3 className="gcw:text-error gcw:transition-colors gcw:group-hover:text-white" size={16} />
                    }
                  />
                )}
              </div>
            )}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>

      <div className="order-totals-section">
        <OrderItemTotals order={order} currency={currency} countryCode={countryCode} />
        {!hidePaid && (
          <div className="gcw:flex gcw:justify-between">
            <span className="gcw:text-order-details gcw:mb-0">{t("paid", "Total paid")}:</span>
            <span className="gcw:text-order-details gcw:mb-0">
              {formatCurrency(order.amount_paid ?? 0, currency, countryCode)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrderItem;
